/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext } from "react";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import { Box, Chip, Typography, Button } from "@mui/material";
import { classNames } from "../utils/utils";
import TradingSymbol from "./TradingSymbol";
import { AuthContext } from "../contexts/AuthContext";
import IntradayTradeModal from "./IntradayTradeModal";
import { getIntradaySettings } from "../services/scannerService";
import { useEffect, useState, useMemo, useCallback } from "react";

interface IntradayResult {
  _id: string;
  tradingsymbol: string;
  instrument_token: number;
  category?: string;
  gapPct: number;
  preOpenVolume: number;
  preOpenVolRatio: number;
  relativeStrength: number;
  trendBias: string;
  baselineScore?: number;
  score: number;
  grade: string;
  bias: string;
  direction?: "LONG" | "SHORT" | string;
  preOpenPrice: number;
  prevClose: number;
  timestamp: Date;
  fundamentals?: any;
  sector?: string;
  industry?: string;
  capCategory?: string;
  orh?: number;
  orl?: number;
  vwap?: number;
  vwapSlope?: number;
  lastLTP?: number;
  
  // Phase 1/2 Multi-Day S/R (Shadow Telemetry)
  high20?: number;
  low20?: number;
  high50?: number;
  high52w?: number;
  overheadResistance?: number;
  supportFloor?: number;
  distanceToResistancePct?: number;
  distanceToSupportPct?: number;
  isBlueSky?: boolean;
  isBlueSky50?: boolean;
  isBlueSky52w?: boolean;
  isMultiDayBreakdown?: boolean;
  shadowSrAction?: "BOOST" | "NEUTRAL" | "CAUTION" | "BLOCKED";
  shadowSrScoreDelta?: number;
}

const IntradayScannerTable = ({
  results,
  isLoading,
  prices,
}: {
  results: IntradayResult[];
  isLoading: boolean;
  prices: Record<number, { price: number; ohlcClose?: number, change?: number }>;
}) => {
  const authContext = useContext(AuthContext);
  const [tradeModal, setTradeModal] = useState<{ open: boolean; stock: any }>({ open: false, stock: null });
  const [baseRisk, setBaseRisk] = useState(10000); // Default, will be fetched

  useEffect(() => {
    async function initSettings() {
      const settings = await getIntradaySettings();
      if (settings.baseRisk) {
        setBaseRisk(settings.baseRisk);
      }
    }
    initSettings();
  }, []);

  const isAdmin = authContext?.isAdmin;

  const columns: GridColDef<IntradayResult>[] = useMemo(() => [
    {
      field: "tradingsymbol",
      headerName: "Symbol",
      flex: 0.5,
      renderCell: (params) => (<span style={{ fontWeight: params.value > 1 ? 700 : 400 }}>{params.row.tradingsymbol}</span>
        // <TradingSymbol symbol={params.row.tradingsymbol} />
      ),
    },
    {
      field: "trendBias",
      headerName: "Trend",
      flex: 0.5,
      renderCell: (params) => (
        <Chip
          label={params.value || "—"}
          color={params.value === "UP" ? "success" : params.value === "DOWN" ? "error" : "default"}
          size="small"
          variant="outlined"
          sx={{ fontSize: "10px", height: "20px" }}
        />
      ),
    },
    {
      field: "baselineScore",
      headerName: "Baseline Score",
      flex: 0.5,
      renderCell: (params) => <>{params.value !== undefined && params.value !== null ? Math.round(params.value) : "—"}</>,
    },
    {
      field: "grade",
      headerName: "Grade",
      flex: 0.5,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={
            params.value === "A+"
              ? "success"
              : params.value === "A"
              ? "info"
              : params.value === "B"
              ? "warning"
              : "default"
          }
          size="small"
        />
      ),
    },
    {
      field: "score",
      headerName: "Score",
      flex: 0.5,
      renderCell: (params) => <>{Math.round(params.value)}</>,
    },
    {
      field: "gapPct",
      headerName: "Gap %",
      flex: 0.5,
      renderCell: (params) => {
        const isUp = params.value >= 0;
        return (
          <div className={classNames(isUp ? "upColor" : "downColor", "percentCt")}>
            <span
              style={{ fontSize: "12px", paddingLeft: "10px", textAlign: "right", float: "right" }}
              className={isUp ? "percentUp" : "percentDown"}
            >
              {isUp ? "+" : ""}{(params.value || 0).toFixed(2)}
            </span>
          </div>
        );
      },
    },
    {
      field: "preOpenVolRatio",
      headerName: "Vol Ratio",
      flex: 0.5,
      renderCell: (params) => (
        <span style={{ fontWeight: params.value > 1 ? 700 : 400 }}>
          {(params.value || 0).toFixed(2)}x
        </span>
      ),
    },
    // {
    //   field: "risk",
    //   headerName: "Risk (₹)",
    //   flex: 0.5,
    //   renderCell: (params) => {
    //     const risk = baseRisk * (params.row.score / 100);
    //     return (
    //         <span style={{ fontWeight: 500, color: "#ea580c" }}>
    //             ₹{Math.round(risk)}
    //         </span>
    //     );
    //   },
    // },
    {
      field: "relativeStrength",
      headerName: "RS vs Index",
      flex: 0.5,
      renderCell: (params) => (
        <span style={{ 
          color: params.value >= 0 ? "#22c55e" : "#ef4444",
          fontWeight: 700 
        }}>
          {(params.value || 0) > 0 ? "+" : ""}{(params.value || 0).toFixed(2)}
        </span>
      ),
    },
    // {
    //   field: "trendBias",
    //   headerName: "Trend",
    //   flex: 0.5,
    //   renderCell: (params) => (
    //     <Chip
    //       label={params.value}
    //       variant="outlined"
    //       size="small"
    //       color={params.value === "UP" ? "success" : params.value === "DOWN" ? "error" : "default"}
    //     />
    //   ),
    // },
    // {
    //     field: "peRatio",
    //     headerName: "PE",
    //     renderCell: (params) => {
    //         const val = params.row.fundamentals?.summaryDetail?.trailingPE?.raw || params.row.fundamentals?.summaryDetail?.trailingPE;
    //         return (
    //             <span style={{ fontWeight: val && val < 25 ? 700 : 400 }}>
    //                 {val ? Number(val).toFixed(1) : "-"}
    //             </span>
    //         );
    //     }
    // },
    // {
    //     field: "roe",
    //     headerName: "ROE",
    //     renderCell: (params) => {
    //         const roe = params.row.fundamentals?.financialData?.returnOnEquity?.raw || params.row.fundamentals?.financialData?.returnOnEquity;
    //          return (
    //             <span style={{ 
    //                 color: roe && roe > 0.15 ? '#22c55e' : 'inherit',
    //                 fontWeight: roe && roe > 0.20 ? 700 : 400
    //               }}>
    //                 {roe ? `${(roe * 100).toFixed(1)}%` : "-"}
    //             </span>
    //         );
    //     }
    // },
    {
        field: "livePrice",
        headerName: "Live Price",
        flex: 0.5,
        renderCell: (params) => {
            const token = Number(params.row.instrument_token);
            const liveData = prices?.[token];
            const livePrice = liveData?.price;
            const displayPrice = livePrice || params.row.lastLTP || params.row.preOpenPrice || params.row.prevClose || 0;
            
            // Priority for coloring: 
            // 1. WebSocket Change
            // 2. WebSocket Price vs OHLC Close
            // 3. Backend Price vs Prev Close
            let isUp = true;
            let isNeutral = true;

            if (liveData?.change !== undefined) {
                isUp = liveData.change >= 0;
                isNeutral = false;
            } else if (livePrice && liveData?.ohlcClose) {
                isUp = livePrice >= liveData.ohlcClose;
                isNeutral = false;
            } else if (params.row.prevClose) {
                isUp = displayPrice >= params.row.prevClose;
                isNeutral = false;
            }

            const colorClass = isNeutral ? "" : (isUp ? "upColor" : "downColor");
            const priceClass = isNeutral ? "" : (isUp ? "priceUp" : "priceDown");
            
            return (
                <div className={classNames(colorClass, "percentCt")}>
                    <span className={priceClass}>
                        {displayPrice.toFixed(2)}
                    </span>
                </div>
            );
        },
    },
    {
        field: "livePercentage",
        headerName: "Live %",
        flex: 0.5,
        renderCell: (params) => {
            const token = Number(params.row.instrument_token);
            const liveData = prices?.[token];
            const livePrice = liveData?.price;
            const liveChange = liveData?.change;
            
            let pnl: string;
            let isUp = true;
            let isNeutral = true;

            if (liveChange !== undefined) {
                pnl = liveChange.toFixed(2);
                isUp = liveChange >= 0;
                isNeutral = false;
            } else if (livePrice && params.row.prevClose) {
                const diff = livePrice - params.row.prevClose;
                pnl = ((diff / params.row.prevClose) * 100).toFixed(2);
                isUp = diff >= 0;
                isNeutral = false;
            } else if (params.row.lastLTP && params.row.prevClose) {
                const diff = params.row.lastLTP - params.row.prevClose;
                pnl = ((diff / params.row.prevClose) * 100).toFixed(2);
                isUp = diff >= 0;
                isNeutral = false;
            } else {
                pnl = (params.row.gapPct || 0).toFixed(2);
                isUp = Number(pnl) >= 0;
                isNeutral = params.row.gapPct === undefined;
            }

            const colorClass = isNeutral ? "" : (isUp ? "upColor" : "downColor");
            const percentClass = isNeutral ? "" : (isUp ? "percentUp" : "percentDown");

            return (
                <div className={classNames(colorClass, "percentCt")}>
                    <span
                        style={{ fontSize: "12px", paddingLeft: "10px", textAlign: "right", float: "right" }}
                        className={percentClass}
                    >
                        {!isNeutral && Number(pnl) > 0 ? "+" : ""}{pnl}
                    </span>
                </div>
            );
        },
    },
    {
        field: "preOpenPrice",
        headerName: "Pre-Open",
        flex: 0.5,
        renderCell: (params) => (params.value || 0).toFixed(2),
    },
    // {
    //     field: "timestamp",
    //     headerName: "Time",
    //     flex: 0.5,
    //     valueFormatter: (value: any) => value ? new Date(value).toLocaleTimeString() : "-",
    // },
    {
        field: "orh",
        headerName: "ORH",
        flex: 0.5,
        renderCell: (params) => params.value ? params.value.toFixed(2) : "-",
    },
    {
        field: "orl",
        headerName: "ORL",
        flex: 0.5,
        renderCell: (params) => params.value ? params.value.toFixed(2) : "-",
    },
    {
        field: "vwap",
        headerName: "VWAP",
        flex: 0.5,
        renderCell: (params) => params.value ? params.value.toFixed(2) : "-",
    },
    {
        field: "vwapSlope",
        headerName: "Slope",
        flex: 0.5,
        renderCell: (params) => params.value !== undefined ? params.value.toFixed(4) : "-",
    },
    {
        field: "distanceToResistancePct",
        headerName: "S/R Runway (Shadow)",
        flex: 0.7,
        renderCell: (params) => {
            const row = params.row;
            const isLong = (row.direction || 'LONG') === 'LONG';
            const isBlueSky = row.isBlueSky;
            const isBlueSky52w = row.isBlueSky52w;
            const isBreakdown = row.isMultiDayBreakdown;
            const distRes = row.distanceToResistancePct;
            const distSup = row.distanceToSupportPct;
            const overheadRes = row.overheadResistance;
            const supportFloor = row.supportFloor;

            if (isLong) {
                if (isBlueSky52w) {
                    return (
                        <Chip
                            label="🌌 52W Sky"
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "10px", height: "20px", borderColor: "#818cf8", color: "#6366f1", borderStyle: "dashed", fontWeight: 600 }}
                        />
                    );
                }
                if (isBlueSky) {
                    return (
                        <Chip
                            label="🚀 20D Sky"
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "10px", height: "20px", borderColor: "#38bdf8", color: "#0284c7", borderStyle: "dashed", fontWeight: 600 }}
                        />
                    );
                }
                if (distRes !== undefined && distRes !== 999) {
                    const isCeilingTrap = distRes < 0.8;
                    return (
                        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", lineHeight: 1.1 }}>
                            <span style={{ fontSize: "11px", fontWeight: isCeilingTrap ? 700 : 500, color: isCeilingTrap ? "#d97706" : "#64748b" }}>
                                {isCeilingTrap ? "⚠️ " : ""}₹{overheadRes?.toFixed(1) || "—"} ({distRes.toFixed(1)}%)
                            </span>
                        </Box>
                    );
                }
            } else {
                // Short candidate
                if (isBreakdown) {
                    return (
                        <Chip
                            label="📉 Breakdown"
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "10px", height: "20px", borderColor: "#f87171", color: "#ef4444", borderStyle: "dashed", fontWeight: 600 }}
                        />
                    );
                }
                if (distSup !== undefined && distSup !== 999) {
                    const isFloorTrap = distSup < 0.8;
                    return (
                        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", lineHeight: 1.1 }}>
                            <span style={{ fontSize: "11px", fontWeight: isFloorTrap ? 700 : 500, color: isFloorTrap ? "#d97706" : "#64748b" }}>
                                {isFloorTrap ? "⚠️ " : ""}Floor: ₹{supportFloor?.toFixed(1) || "—"} ({distSup.toFixed(1)}%)
                            </span>
                        </Box>
                    );
                }
            }

            return <span style={{ color: "#94a3b8", fontSize: "11px" }}>—</span>;
        },
    },
    // {
    //     field: "action",
    //     headerName: "Action",
    //     flex: 0.5,
    //     renderCell: (params) => {
    //         const grade = params.row.grade;
    //         const canTrade = grade === "A+" || grade === "A";
    //         if (!isAdmin || !canTrade) return null;

    //         return (
    //             <Button
    //                 variant="contained"
    //                 size="small"
    //                 color="primary"
    //                 onClick={() => setTradeModal({ open: true, stock: params.row })}
    //                 sx={{ fontSize: "10px", py: 0 }}
    //             >
    //                 Trade
    //             </Button>
    //         );
    //     }
    // }
  ], [prices, isAdmin, baseRisk]);

  return (
    <Box sx={{ height: 750, width: "100%" }}>
      <DataGrid
        loading={isLoading}
        rows={results}
        columns={columns}
        getRowId={(row) => row._id}
        getRowClassName={useCallback((params: any) => {
          const token = Number(params.row.instrument_token);
          const livePrice = prices?.[token]?.price;
          const orh = params.row.orh;
          if (orh && livePrice && livePrice > orh) {
            return "best-pick-row";
          }
          return "";
        }, [prices])}
        slots={{ toolbar: GridToolbar }}
        initialState={{
          pagination: { paginationModel: { page: 0, pageSize: 25 } },
          sorting: { sortModel: [{ field: "score", sort: "desc" }] },
        }}
        pageSizeOptions={[10, 25, 50]}
        sx={{
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "primary.main",
            width: "auto"
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        }}
      />
      <IntradayTradeModal
        open={tradeModal.open}
        onClose={() => setTradeModal({ open: false, stock: null })}
        stock={tradeModal.stock}
        baseRisk={baseRisk}
        livePrice={prices?.[Number(tradeModal.stock?.instrument_token)]?.price || tradeModal.stock?.preOpenPrice}
      />
    </Box>
  );
};

export default IntradayScannerTable;
